import type { DetectorEvent, DetectorEventType } from "./types.ts";

type Handler<E> = (event: E) => void;

type EventOf<K extends DetectorEventType> = Extract<DetectorEvent, { type: K }>;

/**
 * The contract from spec §4: the detector emits, everything else subscribes.
 *
 * This is deliberately the dumbest possible pub/sub. Its value is not in what it
 * does but in what it prevents — a game prototype can never reach into the
 * detector, so five mechanics can be tried in an afternoon without any of them
 * leaving a mark on the signal path.
 */
export class EventBus {
  #handlers = new Map<string, Set<Handler<never>>>();
  #any = new Set<Handler<DetectorEvent>>();

  on<K extends DetectorEventType>(type: K, handler: Handler<EventOf<K>>): () => void {
    let set = this.#handlers.get(type);
    if (!set) {
      set = new Set();
      this.#handlers.set(type, set);
    }
    set.add(handler as Handler<never>);
    return () => set.delete(handler as Handler<never>);
  }

  onAny(handler: Handler<DetectorEvent>): () => void {
    this.#any.add(handler);
    return () => this.#any.delete(handler);
  }

  emit(event: DetectorEvent): void {
    const set = this.#handlers.get(event.type);
    if (set) {
      for (const handler of set) (handler as Handler<DetectorEvent>)(event);
    }
    for (const handler of this.#any) handler(event);
  }

  clear(): void {
    this.#handlers.clear();
    this.#any.clear();
  }
}
